'use client';

import { FC, useContext, useState, type ChangeEvent, useMemo } from 'react';
import { updateCompany } from '@/_api/admin-companies';
import { ConfirmChangingEntityEmailDialogProps } from './ConfirmChangingEntityEmailDialog.types';
import {
  Button,
  ButtonGroup,
  Dialog,
  Select,
  Flex,
  SelectItem,
} from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider/CompanyProvider';

export const ConfirmChangingEntityEmailDialog: FC<
  ConfirmChangingEntityEmailDialogProps
> = ({ newEmail, onClose }) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [selectedEntityId, setSelectedEntityId] = useState<number | undefined>(
    undefined,
  );

  const { token } = useContext(UserContext);
  const { selectedUser, selectedUserCompanies } = useContext(CompanyContext);
  const { addToast } = useContext(ToastContext);

  const relatedEntityItems: Array<SelectItem> = useMemo(() => {
    if (!selectedUser?.id) return [];

    return selectedUserCompanies.map((company) => ({
      name: company.name,
      value: company.id.toString(),
    }));
  }, [selectedUser?.id, selectedUserCompanies]);

  const entityId: number | undefined =
    selectedEntityId ?? parseInt(relatedEntityItems?.[0]?.value || '0');

  const onUpdateEntityEmail = async () => {
    try {
      setLoading(true);

      await updateCompany({
        companyId: entityId,
        userId: selectedUser?.id!,
        business_email: newEmail,
        token,
      });
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const onEntitySelect = (event: ChangeEvent<HTMLSelectElement>) =>
    setSelectedEntityId(parseInt(event.target.value));

  return (
    <Dialog
      isOpen={!!newEmail && relatedEntityItems.length > 0}
      onClose={onClose}
      title="Should we also update the Entity's Entity Email?"
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          <Select
            items={relatedEntityItems}
            label="Select Entity"
            onChange={onEntitySelect}
            selectedValue={
              selectedEntityId?.toString() ||
              relatedEntityItems?.[0]?.value ||
              ''
            }
            isDisabled={isLoading}
          />
          <ButtonGroup align="right">
            <Button
              intent="secondary"
              value="Only update User Email"
              isDisabled={isLoading}
              onClick={onClose}
            />
            <Button
              intent="primary"
              value="Update Entity Email"
              isDisabled={isLoading}
              onClick={onUpdateEntityEmail}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
